import * as React from 'react'

import { Box, type SortDirection, Table, Text } from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'

import createMemo from 'modules/core/hooks/createMemo'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import EmployeeLabel from 'modules/reviews/components/EmployeeLabel'
import { REVIEW_MINIMUM_ANSWERS_TO_SHOW_ANONYMOUS } from 'modules/reviews/lib/constants'
import { getGroupName } from 'modules/reviews/lib/getGroupName'
import type { Reviewers } from 'modules/reviews/lib/types'
import groups from 'modules/reviews/store/collections/groups'
import type Group from 'modules/reviews/store/models/Group'

import Settings from './Settings'

interface ItemProps {
  user: User
  group: Group
  canHaveSettings: boolean
}

function Item({ user, group, canHaveSettings }: ItemProps) {
  const name = getGroupName(group)

  return (
    <Table.Row>
      <Table.Cell colSpan={2}>
        <EmployeeLabel user={user} />
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Text.Normal>{name}</Text.Normal>
      </Table.Cell>
      <Table.Cell>
        <Box alignItems="flexEnd">
          {canHaveSettings && <Settings user={user} accessGroup={group.accessGroup} />}
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

const useFilter = createMemo((reviewers: Reviewers, search: string) =>
  reviewers
    .map((reviewer) => {
      const user = users.get(reviewer.access_id)
      const group = groups.get(reviewer.surveys_group_id)

      if (!user || !group) return null

      if (!user.matchesFullName(search)) return null

      return {
        user,
        group,
      }
    })
    .filter(Boolean)
)

interface Props {
  search: string
  reviewers: Reviewers
  isAnonymous: boolean | undefined
  direction: SortDirection
  attribute: string
}

export default function Body({ search, reviewers, isAnonymous, direction, attribute }: Props) {
  const filteredReviewers = useFilter(reviewers, search)
  const hasEnoughReviewers = reviewers.length > REVIEW_MINIMUM_ANSWERS_TO_SHOW_ANONYMOUS
  const canHaveSettings = isAnonymous ? hasEnoughReviewers : true
  const sortedReviewers = orderBy(
    filteredReviewers,
    (reviewer) =>
      attribute === 'reviewer' ? reviewer?.user?.legalName : getGroupName(reviewer?.group),
    [direction]
  )

  return (
    <Table.Body>
      {sortedReviewers.length === 0 && (
        <Table.Blank colSpan={5} text={i18n.t('surveys.review.participants.table.body.empty')} />
      )}
      {sortedReviewers.map(({ user, group }) => {
        const name = getGroupName(group)

        return (
          <Item
            key={`${user.attributes.id} - ${name}`}
            user={user}
            group={group}
            canHaveSettings={canHaveSettings}
          />
        )
      })}
    </Table.Body>
  )
}
